/**
 * Validation utility functions for the SmartTrack application
 */

/**
 * Validates if a string is a properly formatted MAC address
 * @param mac - The MAC address string to validate
 * @returns true if the MAC address is valid, false otherwise
 */
export const isValidMacAddress = (mac: string): boolean => {
  // MAC address format: XX:XX:XX:XX:XX:XX (6 pairs of hex digits separated by colons)
  const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
  return macRegex.test(mac);
};

/**
 * Formats a MAC address input to standard format (XX:XX:XX:XX:XX:XX)
 * @param input - The input string to format
 * @returns The formatted MAC address string
 */
export const formatMacAddress = (input: string): string => {
  // Extract MAC from scanner format (e.g., "MAC:BC5729047458,SERIAL:32046;")
  const scannerMatch = input.match(/MAC:([0-9A-Fa-f]{12})\b/);
  if (scannerMatch) {
    return scannerMatch[1].match(/.{2}/g)!.join(':').toUpperCase();
  }

  // Remove all non-hex characters
  const hexOnly = input.replace(/[^0-9A-Fa-f]/g, '');
  
  // If we have exactly 12 hex characters, format with colons
  if (hexOnly.length === 12) {
    return hexOnly.match(/.{2}/g)?.join(':').toUpperCase() || input;
  }
  
  return input;
};

/**
 * Gets a user-friendly error message for MAC address validation
 * @returns The error message string
 */
export const getMacAddressErrorMessage = (): string => {
  return 'Invalid format';
};

/**
 * Gets helper text for MAC address input fields
 * @returns The helper text string
 */
export const getMacAddressHelperText = (): string => {
  return 'Enter MAC address in format: XX:XX:XX:XX:XX:XX';
};

/**
 * Gets placeholder text for MAC address input fields
 * @returns The placeholder text string
 */
export const getMacAddressPlaceholder = (): string => {
  return '00:11:22:33:44:55';
};

/**
 * Represents a parsed and validated tag
 */
export interface ParsedTag {
  original: string;
  formatted: string;
  isValid: boolean;
  error?: string;
  isDuplicate?: boolean;
  isExisting?: boolean;
}

// TODO: Add global beacon validation to prevent assigning a beacon to multiple SKUs.
// Currently validation only checks within the current SKU. A beacon assigned to a different
// SKU would not be detected, potentially causing duplicate assignments.
// When implementing:
// 1. Fetch all beacon assignments globally
// 2. Check if beacon is assigned to a DIFFERENT SKU (warn about reassignment)
// 3. Handle reassignment by removing old assignment before creating new one

/**
 * Parses and validates multiple MAC addresses from newline-separated input
 * @param input - Newline-separated MAC addresses
 * @param existingTags - Optional array of already-assigned tags to check for duplicates
 * @param initialTags - Optional array of tags that belong to the current assignment (edit mode)
 * @returns Array of ParsedTag objects with validation results
 */
export const parseAndValidateTags = (input: string, existingTags: string[] = [], initialTags: string[] = []): ParsedTag[] => {
  const lines = input
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0);

  const existingSet = new Set(existingTags.map(t => t.toUpperCase()));
  const initialSet = new Set(initialTags.map(t => t.toUpperCase()));
  const seenInInput = new Set<string>();
  const results: ParsedTag[] = [];

  for (const line of lines) {
    const formatted = formatMacAddress(line);
    const formattedUpper = formatted.toUpperCase();
    const isValid = isValidMacAddress(formatted);

    let error: string | undefined;
    let isDuplicate = false;
    const isExisting = initialSet.has(formattedUpper);

    if (!isValid) {
      error = getMacAddressErrorMessage();
    } else if (existingSet.has(formattedUpper)) {
      isDuplicate = true;
      error = 'Tag already assigned';
    } else if (seenInInput.has(formattedUpper)) {
      isDuplicate = true;
      error = 'Duplicate tag';
    }

    if (isValid && !isDuplicate) {
      seenInInput.add(formattedUpper);
    }

    results.push({
      original: line,
      formatted,
      isValid,
      error,
      isDuplicate,
      isExisting,
    });
  }

  return results;
};

/**
 * Extracts only valid, non-duplicate tags from parsed results
 * @param parsed - Array of ParsedTag objects
 * @returns Array of formatted MAC address strings
 */
export const getValidTags = (parsed: ParsedTag[]): string[] => {
  return parsed
    .filter(tag => tag.isValid && !tag.isDuplicate)
    .map(tag => tag.formatted);
};

/**
 * Checks if any tags in the parsed results are invalid or duplicates
 * @param parsed - Array of ParsedTag objects
 * @returns true if any tag is invalid or a duplicate
 */
export const hasInvalidTags = (parsed: ParsedTag[]): boolean => {
  return parsed.some(tag => !tag.isValid || tag.isDuplicate);
};

/**
 * Computes the delta between current tags and existing tags (case-insensitive).
 * Returns only tags that are NOT in the existing set.
 * @param currentTags - Array of current tag IDs from user input
 * @param existingTags - Array of tag IDs that already exist
 * @returns Array of only new tag IDs (not in existing)
 */
export const computeNewTags = (currentTags: string[], existingTags: string[]): string[] => {
  const existingSet = new Set(existingTags.map(t => t.toUpperCase()));
  return currentTags.filter(tag => !existingSet.has(tag.toUpperCase()));
};
